import { useMutation } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';

import { Footer } from '@/components/layout/footer';
import { Logo } from '@/components/layout/logo';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { PasswordField } from '@/components/ui/password-field';
import { isStrongPassword } from '@/lib/validation';

import { completePasswordReset } from '../api/auth-api';
import { FORM_ERROR, errorMessage } from '../use-auth-error';

interface NewPasswordForm {
  password: string;
  confirmPassword: string;
}

export function NewPasswordPage() {
  const { key = '' } = useParams();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitted },
  } = useForm<NewPasswordForm>();

  const mutation = useMutation({
    mutationFn: (v: NewPasswordForm) => completePasswordReset(key, v.password),
    onSuccess: () => void navigate('/sign-in', { replace: true, state: { newPassword: true } }),
  });

  const hasFieldErrors = isSubmitted && Object.keys(errors).length > 0;
  const password = useWatch({ control, name: 'password' }) ?? '';

  return (
    <div className="public">
      <div className="public__body" style={{ gridTemplateColumns: '1fr', maxWidth: '36rem' }}>
        <div className="stack">
          <Logo />
          <h2>Create a new password</h2>
          <hr className="divider divider--gray" />
          {mutation.isError && <Alert>{errorMessage(mutation.error)}</Alert>}
          {!mutation.isError && hasFieldErrors && <Alert>{FORM_ERROR}</Alert>}
          <form
            className="stack"
            noValidate
            onSubmit={(e) =>
              void handleSubmit((v) => {
                mutation.mutate(v);
              })(e)
            }
          >
            <PasswordField
              id="newPassword-password"
              label="Password"
              autoComplete="new-password"
              showRequirements
              currentValue={password}
              error={errors.password?.message}
              {...register('password', {
                required: 'Please provide a valid password.',
                validate: (p) => isStrongPassword(p) || 'Please provide a valid password.',
              })}
            />
            <PasswordField
              id="newPassword-confirmPassword"
              label="Confirm Password"
              autoComplete="new-password"
              error={errors.confirmPassword?.message}
              {...register('confirmPassword', {
                validate: (c) => c === password || 'Passwords must match.',
              })}
            />
            <Button type="submit" block busy={mutation.isPending}>
              Submit
            </Button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
